import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { PropertyListings } from '@/types/PropertyListings';
import { Badge } from '@/components/ui/badge';
import Helper from '@/lib/helper';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Edit, Trash, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface _PendingListingTable {
    loading: boolean;
    listings: PropertyListings[] | null;
    startIndex?: number;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedListingId: React.Dispatch<React.SetStateAction<number | null>>;
    setActionType: React.Dispatch<React.SetStateAction<'delete' | 'deactivate' | 'close' | 'reopen' | 'activate' | 'repost'>>;
}

const PendingListingTable: React.FC<_PendingListingTable> = ({
    loading,
    listings,
    startIndex = 0,
    setOpen,
    setSelectedListingId,
    setActionType,
}) => {
    const triggerAction = (id: number, type: 'delete') => {
        setSelectedListingId(id);
        setActionType(type);
        setOpen(true);
    };

    return (
        <TooltipProvider delayDuration={0}>
            <Table className='mt-5'>
                <TableHeader>
                    <TableRow>
                        <TableHead className='w-[500px]'>Listing</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <div className='flex justify-center py-4'>
                                    <Spinner />
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : listings && listings.length > 0 ? (
                        listings.map((listing, index) => (
                            <TableRow key={listing.id}>
                                <TableCell className='w-[500px]'>
                                    <div className='flex'>
                                        <span className='w-6 mr-2 flex-shrink-0 self-center text-right text-xs font-medium text-gray-400 tabular-nums'>
                                            {startIndex + index + 1}
                                        </span>
                                        <div
                                            className='rounded-lg h-[100px] w-[100px] flex-shrink-0'
                                            style={{
                                                background: `url(${import.meta.env.VITE_AWS_S3}/${
                                                    listing.medias.find(x => x.mediaType === 1 && x.isCover === 1)?.path ||
                                                    listing.medias.find(x => x.mediaType === 1)?.path
                                                })`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center center',
                                            }}
                                        />
                                        <div className='px-3'>
                                            <div>{listing.headline}</div>
                                            <div className='text-xs mt-1 whitespace-break-spaces'>{listing.rawAddress}</div>
                                            <div className='font-bold mt-2'>{Helper.formatCurrency(Number(listing.price))}</div>
                                            <div className='flex items-center gap-2 mt-1.5'>
                                                <Badge variant={listing.forSaleOrRentId === 1 ? 'outline' : 'secondary'}>
                                                    {listing.forSaleOrRentId === 1 ? 'Sale' : 'Rent'}
                                                </Badge>
                                                {listing.createChannel === 'ai' && (
                                                    <Badge variant='outline' className='gap-1 border-violet-200 bg-violet-50 text-violet-700' title='Created with AI'>
                                                        <Sparkles className='h-3 w-3' /> AI
                                                    </Badge>
                                                )}
                                                <span className='text-[11px] text-gray-700 font-mono bg-gray-100 px-1.5 py-0.5 rounded'>
                                                    {listing.listingReferenceNumber}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge
                                    className={
                                        listing.isInternal === 1
                                        ? "bg-blue-100 border-blue-700 text-blue-700"
                                        : "bg-green-100 border-green-700 text-green-700"
                                    }
                                    >
                                    {listing.isInternal === 1 ? "Internal" : "Public"}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className='flex flex-nowrap items-center gap-2'>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type='button'
                                                    size='sm'
                                                    variant='outline'
                                                    asChild
                                                >
                                                    <Link to={`/listings/edit/${listing.id}`}>
                                                        <Edit className='h-3.5 w-3.5' />
                                                        Edit
                                                    </Link>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Edit Listing</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type='button'
                                                    size='sm'
                                                    variant='destructive'
                                                    onClick={() => triggerAction(listing.id, 'delete')}
                                                >
                                                    <Trash className='h-3.5 w-3.5' />
                                                    Delete
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Delete Listing</TooltipContent>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={3} className="text-center py-4">
                                No listings found.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TooltipProvider>
    );
};

export default PendingListingTable;
